(() => {
  "use strict";

  const ACCEPTED_TYPES = [
    "image/*",
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".gif",
    ".bmp",
    ".tif",
    ".tiff",
    ".heic",
    ".heif",
    ".avif",
    ".svg",
    ".psd",
  ].join(",");
  const UPLOADCARE_PUBLIC_KEY = "259e94149a4415a3b0b0";
  const LOCAL_PREVIEW_MAX_BYTES = 12 * 1024 * 1024;

  function qs(root, selector) {
    return root.querySelector(selector);
  }

  function fileExtension(file) {
    const name = file && file.name ? file.name : "";
    const match = name.match(/\.([^.]+)$/);
    return match ? match[1].toLowerCase() : "";
  }

  function canBrowserPreview(file) {
    const extension = fileExtension(file);
    if (["tif", "tiff", "heic", "heif", "psd"].includes(extension)) return false;
    if (!file || !file.type) return !extension || ["jpg", "jpeg", "png", "webp", "gif", "bmp", "avif", "svg"].includes(extension);
    return file.type.startsWith("image/") && !file.type.includes("tiff") && !file.type.includes("heic") && !file.type.includes("heif") && !file.type.includes("photoshop");
  }

  function status(studio, message) {
    const el = qs(studio, "[data-status]");
    if (el && message) el.textContent = message;
  }

  function cloudinaryConfig(studio) {
    const cloudName = (studio.dataset.cloudinaryCloudName || "").trim();
    const uploadPreset = (studio.dataset.cloudinaryUploadPreset || "").trim();
    const folder = (studio.dataset.cloudinaryFolder || "905print-orders").trim();
    return {
      enabled: Boolean(cloudName && uploadPreset),
      cloudName,
      uploadPreset,
      folder,
    };
  }

  function transformedCloudinaryUrl(config, response) {
    if (!config.enabled || !response || !response.public_id) return "";
    const version = response.version ? `v${response.version}/` : "";
    const publicId = String(response.public_id).split("/").map(encodeURIComponent).join("/");
    return `https://res.cloudinary.com/${encodeURIComponent(config.cloudName)}/image/upload/f_jpg,q_auto,w_1800/${version}${publicId}.jpg`;
  }

  function ensureRemoteInput(form, name, marker, value) {
    const input = ensureHiddenInput(form, name, marker);
    input.value = value || "";
    input.disabled = !value;
    return input;
  }

  async function uploadToCloudinary(studio, file) {
    const config = cloudinaryConfig(studio);
    if (!config.enabled || !file) return null;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", config.uploadPreset);
    if (config.folder) formData.append("folder", config.folder);

    status(studio, "Uploading original artwork for production...");
    const response = await fetch(`https://api.cloudinary.com/v1_1/${encodeURIComponent(config.cloudName)}/auto/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) throw new Error("Cloudinary upload failed.");
    const data = await response.json();
    data.preview_url = transformedCloudinaryUrl(config, data);
    return data;
  }

  function ensureRemoteUpload(studio, file) {
    const config = cloudinaryConfig(studio);
    if (!config.enabled || !file) return null;
    if (studio.__remoteUploadFile === file && studio.__remoteUploadPromise) return studio.__remoteUploadPromise;

    studio.__remoteUploadFile = file;
    studio.__remoteUploadResult = null;
    studio.__remoteUploadPromise = uploadToCloudinary(studio, file)
      .then((result) => {
        studio.__remoteUploadResult = result;
        return result;
      })
      .catch((error) => {
        studio.__remoteUploadResult = null;
        status(studio, "The file is attached locally, but the preview processor could not upload it.");
        return null;
      });

    return studio.__remoteUploadPromise;
  }

  function loadImageFromUrl(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("The browser could not load the converted preview."));
      image.src = url;
    });
  }

  function setPreviewFromImage(studio, imageUrl, message) {
    if (!imageUrl) return;
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      studio.artRatio = image.naturalWidth / image.naturalHeight;
      studio.artNaturalWidth = image.naturalWidth;
      studio.artNaturalHeight = image.naturalHeight;
      qs(studio, "[data-art-image]").crossOrigin = "anonymous";
      qs(studio, "[data-art-image]").src = imageUrl;
      qs(studio, "[data-art]").classList.add("has-image");
      studio.classList.add("has-artwork");
      if (typeof studio.renderSizes === "function") studio.renderSizes();
      if (typeof studio.updatePreview === "function") studio.updatePreview();
      if (typeof studio.sync === "function") studio.sync();
      status(studio, message || "Preview ready. Original file is stored for production.");
    };
    image.onerror = () => {
      status(studio, "Original file is stored for production, but the converted preview could not load.");
    };
    image.src = imageUrl;
  }

  function queryValue(params, names) {
    for (const name of names) {
      const value = params.get(name);
      if (value) return value;
    }
    return "";
  }

  function artworkFromUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const originalUrl = queryValue(params, ["artwork_url", "artist_artwork_url", "original_artwork_url"]);
    if (!originalUrl) return null;
    const previewUrl = queryValue(params, ["artwork_preview_url", "artist_preview_url", "preview_url"]) || originalUrl;
    const name = queryValue(params, ["artwork_name", "artist_artwork_name", "name"]) || "Saved artwork";
    return {
      originalUrl,
      previewUrl,
      name,
    };
  }

  function preloadArtistLibraryArtwork(studio) {
    if (studio.__artistLibraryArtworkLoaded) return;
    const artwork = artworkFromUrlParams();
    if (!artwork) return;
    studio.__artistLibraryArtworkLoaded = true;
    studio.__artistLibraryArtwork = artwork;
    studio.classList.add("has-artist-library-artwork");
    setPreviewFromImage(studio, artwork.previewUrl, `${artwork.name} loaded. Choose your size and material.`);
  }

  function setPreviewFromFile(studio, file, message) {
    if (!file || !window.URL || !URL.createObjectURL) return false;
    const previewUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      if (studio.__directPreviewObjectUrl) URL.revokeObjectURL(studio.__directPreviewObjectUrl);
      studio.__directPreviewObjectUrl = previewUrl;
      studio.artRatio = image.naturalWidth / image.naturalHeight;
      studio.artNaturalWidth = image.naturalWidth;
      studio.artNaturalHeight = image.naturalHeight;
      qs(studio, "[data-art-image]").src = previewUrl;
      qs(studio, "[data-art]").classList.add("has-image");
      studio.classList.add("has-artwork");
      if (typeof studio.renderSizes === "function") studio.renderSizes();
      if (typeof studio.updatePreview === "function") studio.updatePreview();
      if (typeof studio.sync === "function") studio.sync();
      status(studio, message || "Preview ready. Uploading original artwork for production...");
    };
    image.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      status(studio, "The original file is uploading for production, but this browser could not preview it.");
    };
    image.src = previewUrl;
    return true;
  }

  function ensureUploadlyStyles() {
    if (document.getElementById("print-studio-uploadly-styles")) return;
    const style = document.createElement("style");
    style.id = "print-studio-uploadly-styles";
    style.textContent = ".print-studio__uploadly{display:none;margin-top:14px;padding:16px;border:1px solid rgba(32,34,41,.16);background:#fff}.print-studio.has-uploadly:not(.uses-direct-uploadcare) .print-studio__upload{display:none}.print-studio.has-uploadly .print-studio__uploadly{display:block}.print-studio.uses-direct-uploadcare .print-studio__upload{display:grid}.print-studio.uses-direct-uploadcare .print-studio__uploadly{display:none}.print-studio [data-submit][hidden]{display:none!important}.print-studio.uses-direct-uploadcare [data-submit]:not([hidden]){display:block!important}";
    document.head.append(style);
  }

  function uploadcareShortUrl(uuid) {
    return `https://cdn.upstorage.io/u/${String(uuid || "").split("-").map((part) => part.slice(-3)).join("")}`;
  }

  function uploadcarePreviewUrl(uuid) {
    return `https://ucarecdn.com/${encodeURIComponent(uuid)}/-/preview/1800x1800/`;
  }

  async function uploadToUploadcare(studio, file) {
    if (!UPLOADCARE_PUBLIC_KEY || !file) return null;
    const formData = new FormData();
    formData.append("UPLOADCARE_PUB_KEY", UPLOADCARE_PUBLIC_KEY);
    formData.append("UPLOADCARE_STORE", "1");
    formData.append("file", file);

    status(studio, "Uploading original artwork for production...");
    const response = await fetch("https://upload.uploadcare.com/base/", {
      method: "POST",
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.file) throw new Error("The artwork upload did not finish. Please try again.");

    const uuid = data.file;
    return {
      uuid,
      secure_url: `https://ucarecdn.com/${uuid}/`,
      preview_url: uploadcarePreviewUrl(uuid),
      uploadly_url: uploadcareShortUrl(uuid),
    };
  }

  function ensureUploadcareUpload(studio, file) {
    if (!file) return null;
    if (studio.__uploadcareFile === file && studio.__uploadcarePromise) return studio.__uploadcarePromise;

    studio.__uploadcareFile = file;
    studio.__uploadcareResult = null;
    studio.__uploadcarePromise = uploadToUploadcare(studio, file)
      .then((result) => {
        studio.__uploadcareResult = result;
        status(studio, "Preview ready. Original file is stored for production.");
        return result;
      })
      .catch((error) => {
        studio.__uploadcareResult = null;
        status(studio, error && error.message ? error.message : "The artwork upload did not finish. Please try again.");
        throw error;
      });

    return studio.__uploadcarePromise;
  }

  function releaseUploadlySubmitControl(studio) {
    const submit = qs(studio, "[data-submit]");
    if (!submit) return;
    submit.classList.remove("ph_atc_btn");
    submit.removeAttribute("data-ph-product-id");
    if (submit.form) {
      submit.form.removeAttribute("data-ph-product-id");
      if (submit.form.dataset) delete submit.form.dataset.phProductId;
    }
    if ((submit.getAttribute("style") || "").includes("display")) submit.style.removeProperty("display");
  }

  function watchUploadlySubmitControl(studio) {
    if (studio.__uploadlySubmitWatcherBound) return;
    const submit = qs(studio, "[data-submit]");
    if (!submit) return;
    studio.__uploadlySubmitWatcherBound = true;
    const release = () => releaseUploadlySubmitControl(studio);
    release();
    if (window.MutationObserver) {
      new MutationObserver(release).observe(submit, {
        attributes: true,
        attributeFilter: ["class", "style", "data-ph-product-id"],
      });
      if (submit.form) {
        new MutationObserver(release).observe(submit.form, {
          attributes: true,
          attributeFilter: ["data-ph-product-id"],
        });
      }
    }
  }

  function findStandaloneUploadly(studio) {
    return Array.from(document.querySelectorAll("#photouploadapp, [data-ph-wrap], .uploadcare--widget, [id^='previewImages_']")).find((node) => !studio.contains(node));
  }

  function requestUploadlyInit(studio, mount) {
    if (!mount || mount.dataset.printStudioInitRequested) return;
    mount.dataset.printStudioInitRequested = "true";
    if (mount.id === "photouploadapp" && !mount.classList.contains("photouploadapp")) {
      mount.classList.add("photouploadapp");
    }

    const run = () => {
      try {
        if (typeof window.ph_product_script === "function") window.ph_product_script();
      } catch (error) {
        studio.__uploadlyInitError = error && error.message ? error.message : String(error);
      }
      try {
        if (typeof window.ph_home_script === "function") window.ph_home_script();
      } catch (error) {
        studio.__uploadlyInitError = error && error.message ? error.message : String(error);
      }
    };

    run();
    window.setTimeout(run, 800);
    window.setTimeout(run, 2500);
    window.setTimeout(run, 6000);
  }

  function uploadlyPanel(studio) {
    let panel = qs(studio, "[data-uploadly-panel]");
    if (!panel) {
      const uploadLabel = qs(studio, ".print-studio__upload");
      panel = document.createElement("div");
      panel.className = "print-studio__uploadly";
      panel.setAttribute("data-uploadly-panel", "");
      if (uploadLabel) uploadLabel.after(panel);
      else studio.append(panel);
    }

    const standalone = findStandaloneUploadly(studio);
    if (standalone && !panel.contains(standalone)) {
      const block = standalone.closest(".shopify-block") || standalone;
      panel.append(block);
      requestUploadlyInit(studio, block.querySelector("#photouploadapp") || standalone);
    }

    return panel;
  }

  function uploadlyIsActive(studio) {
    const panel = uploadlyPanel(studio);
    if (!panel) return false;
    return Boolean(panel.querySelector("[data-ph-fieldname], [data-ph-wrap], .uploadcare--widget, .uploadcare--widget__button, [id^='previewImages_'], [id^='selBtn'], input[type='file'], button, iframe"));
  }

  function uploadlyPreviewUrl(studio) {
    const panel = uploadlyPanel(studio);
    if (!panel) return "";
    const image = panel.querySelector("[id^='previewImages_'] img[src], img[src]");
    if (image && image.src) return image.src;
    const link = panel.querySelector("a[href*='ucarecdn.com'], a[href*='uploadcare']");
    return link && link.href ? link.href : "";
  }

  function setUploadlyMode(studio) {
    const active = uploadlyIsActive(studio);
    const upload = qs(studio, "[data-upload]");
    const directUploadcare = Boolean(UPLOADCARE_PUBLIC_KEY);
    studio.classList.toggle("has-uploadly", active || directUploadcare);
    studio.classList.toggle("uses-direct-uploadcare", directUploadcare);
    if (upload) {
      if (active || directUploadcare) {
        upload.removeAttribute("name");
        upload.disabled = false;
      } else {
        upload.name = "properties[_Artwork file]";
      }
    }
    if (directUploadcare) releaseUploadlySubmitControl(studio);
    return active || directUploadcare;
  }

  function bindUploadlyPreview(studio) {
    const panel = uploadlyPanel(studio);
    if (!panel || panel.dataset.uploadlyPreviewBound) return;
    panel.dataset.uploadlyPreviewBound = "true";

    const refresh = () => {
      const active = setUploadlyMode(studio);
      if (!active) return;
      const previewUrl = uploadlyPreviewUrl(studio);
      if (previewUrl && studio.__uploadlyPreviewUrl !== previewUrl) {
        studio.__uploadlyPreviewUrl = previewUrl;
        setPreviewFromImage(studio, previewUrl, "Preview ready. Uploadly is handling the production file.");
      }
    };

    refresh();
    new MutationObserver(refresh).observe(panel, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["src", "href", "class", "style"],
    });
    window.setTimeout(refresh, 800);
    window.setTimeout(refresh, 2500);
    window.setTimeout(refresh, 6000);
  }

  function sizeParts(studio) {
    const size = typeof studio.displaySize === "function" ? studio.displaySize(studio.size) : studio.size;
    const parts = String(size || "").toLowerCase().split("x").map((part) => Number(part.trim()));
    return parts.length === 2 && parts[0] && parts[1] ? parts : [8, 10];
  }

  function ensureHiddenInput(form, name, marker) {
    let input = qs(form, `[data-production-prop="${marker}"]`);
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.setAttribute("data-production-prop", marker);
      form.append(input);
    }
    input.name = name;
    return input;
  }

  function ensureCroppedFileInput(form) {
    let input = qs(form, "[data-cropped-upload]");
    if (!input) {
      input = document.createElement("input");
      input.type = "file";
      input.name = "properties[_Cropped preview file]";
      input.setAttribute("data-cropped-upload", "");
      input.hidden = true;
      input.tabIndex = -1;
      form.append(input);
    }
    return input;
  }

  function updateUploadCopy(studio) {
    const upload = qs(studio, "[data-upload]");
    if (!upload) return;
    upload.accept = ACCEPTED_TYPES;

    const helper = upload.closest(".print-studio__upload") && upload.closest(".print-studio__upload").querySelector("span");
    if (helper) helper.textContent = "JPG, PNG, WebP, TIFF, HEIC, AVIF, SVG, PSD, or most image files. Some formats attach for production but may not preview here.";
  }

  function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error("The browser could not read this file."));
      reader.onload = () => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("The browser could not preview this image type."));
        image.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  function canvasToFile(canvas, filename) {
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("The browser could not create the cropped preview."));
          return;
        }
        resolve(new File([blob], filename, { type: "image/png" }));
      }, "image/png", 0.94);
    });
  }

  function borderPresetInches(mode) {
    if (mode === "border-1") return 1;
    if (mode === "border-2") return 2;
    return 0;
  }

  function borderPresetSideInches(widthInches, borderInches) {
    const maxComfortableBorder = widthInches * 0.12;
    const maxPhysicalBorder = Math.max(0, widthInches / 2 - 0.05);
    return Math.min(borderInches, maxComfortableBorder, maxPhysicalBorder);
  }

  function borderPresetTarget(widthInches, heightInches, imageRatio, outputWidth, outputHeight, borderInches) {
    const sideBorder = borderPresetSideInches(widthInches, borderInches);
    let imageWidthInches = Math.max(0.1, widthInches - sideBorder * 2);
    let imageHeightInches = imageWidthInches / imageRatio;
    let targetXInches = sideBorder;
    let targetYInches = (heightInches - imageHeightInches) / 2;

    if (targetYInches < 0) {
      imageHeightInches = heightInches;
      imageWidthInches = imageHeightInches * imageRatio;
      targetXInches = Math.max(0, (widthInches - imageWidthInches) / 2);
      targetYInches = 0;
    }

    return {
      x: Math.round(outputWidth * (targetXInches / widthInches)),
      y: Math.round(outputHeight * (targetYInches / heightInches)),
      width: Math.max(1, Math.round(outputWidth * (imageWidthInches / widthInches))),
      height: Math.max(1, Math.round(outputHeight * (imageHeightInches / heightInches))),
    };
  }

  async function buildCroppedPreviewFile(studio, file, remotePreviewUrl) {
    if (!file && !remotePreviewUrl) return null;

    const image = remotePreviewUrl ? await loadImageFromUrl(remotePreviewUrl) : await loadImageFromFile(file);
    const [widthInches, heightInches] = sizeParts(studio);
    const ratio = widthInches / heightInches;
    const longSide = 1800;
    const outputWidth = ratio >= 1 ? longSide : Math.max(600, Math.round(longSide * ratio));
    const outputHeight = ratio >= 1 ? Math.max(600, Math.round(longSide / ratio)) : longSide;
    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext("2d");
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, outputWidth, outputHeight);

    const cropMode = studio.cropMode || studio.getAttribute("data-crop-mode") || "fit";
    const imageRatio = image.naturalWidth / image.naturalHeight;
    const borderInches = borderPresetInches(cropMode);
    const borderTarget = borderInches ? borderPresetTarget(widthInches, heightInches, imageRatio, outputWidth, outputHeight, borderInches) : null;
    const targetX = borderTarget ? borderTarget.x : 0;
    const targetY = borderTarget ? borderTarget.y : 0;
    const targetWidth = borderTarget ? borderTarget.width : outputWidth;
    const targetHeight = borderTarget ? borderTarget.height : outputHeight;
    const targetRatio = targetWidth / targetHeight;
    let drawWidth;
    let drawHeight;
    const fitMode = cropMode === "fit" || Boolean(borderTarget);
    if (fitMode ? imageRatio > targetRatio : imageRatio < targetRatio) {
      drawWidth = targetWidth;
      drawHeight = targetWidth / imageRatio;
    } else {
      drawHeight = targetHeight;
      drawWidth = targetHeight * imageRatio;
    }

    const zoomInput = qs(studio, "[data-zoom]");
    const panXInput = qs(studio, "[data-pan-x]") || qs(studio, "[data-crop-x]");
    const panYInput = qs(studio, "[data-pan-y]") || qs(studio, "[data-crop-y]");
    const zoom = zoomInput ? Math.max(0.8, Math.min(1.4, Number(zoomInput.value) / 100 || 1)) : 1;
    const panX = panXInput ? Math.max(-50, Math.min(50, Number(panXInput.value) || 0)) : 0;
    const panY = panYInput ? Math.max(-50, Math.min(50, Number(panYInput.value) || 0)) : 0;
    if (!borderTarget) {
      drawWidth *= zoom;
      drawHeight *= zoom;
    }

    const x = targetX + (targetWidth - drawWidth) * (borderTarget ? 0.5 : 0.5 + panX / 200);
    const y = targetY + (targetHeight - drawHeight) * (borderTarget ? 0.5 : 0.5 + panY / 200);
    context.drawImage(image, x, y, drawWidth, drawHeight);

    const sourceName = file && file.name ? file.name : "artwork";
    const cleanName = sourceName.replace(/\.[^.]+$/, "").replace(/[^a-z0-9_-]+/gi, "-").replace(/^-|-$/g, "") || "artwork";
    return canvasToFile(canvas, `${cleanName}-905print-cropped-preview.png`);
  }

  function applyFilesToInput(input, files) {
    if (!input || !files.length || typeof DataTransfer === "undefined") return false;
    const transfer = new DataTransfer();
    files.forEach((file) => transfer.items.add(file));
    input.files = transfer.files;
    return input.files && input.files.length === files.length;
  }

  function productionSettings(studio, originalFile, croppedGenerated) {
    const zoomInput = qs(studio, "[data-zoom]");
    const panXInput = qs(studio, "[data-pan-x]") || qs(studio, "[data-crop-x]");
    const panYInput = qs(studio, "[data-pan-y]") || qs(studio, "[data-crop-y]");
    const uploadcareResult = studio.__uploadcareResult || null;
    const libraryArtwork = studio.__artistLibraryArtwork || null;
    const libraryOriginalUrl = libraryArtwork && libraryArtwork.originalUrl ? libraryArtwork.originalUrl : "";
    const libraryPreviewUrl = libraryArtwork && libraryArtwork.previewUrl ? libraryArtwork.previewUrl : "";
    const cropMode = studio.cropMode || studio.getAttribute("data-crop-mode") || "fit";
    const borderInches = borderPresetInches(cropMode);
    const borderPreset = borderInches > 0;
    const [widthInches] = sizeParts(studio);
    const appliedSideBorder = borderPreset ? borderPresetSideInches(widthInches, borderInches) : 0;
    const whiteBorder = cropMode === "border-1" ? "1 inch border preset" : cropMode === "border-2" ? "2 inch border preset" : "None";
    return {
      original_file: originalFile ? originalFile.name : libraryArtwork ? libraryArtwork.name : "",
      production_upload: uploadcareResult ? "Uploadcare direct upload" : libraryArtwork ? "Artist library" : setUploadlyMode(studio) ? "Uploadly" : originalFile ? "Shopify line item file" : "",
      original_artwork_url: uploadcareResult && uploadcareResult.uploadly_url ? uploadcareResult.uploadly_url : studio.__remoteUploadResult && studio.__remoteUploadResult.secure_url ? studio.__remoteUploadResult.secure_url : libraryOriginalUrl,
      converted_preview_url: uploadcareResult && uploadcareResult.preview_url ? uploadcareResult.preview_url : studio.__remoteUploadResult && studio.__remoteUploadResult.preview_url ? studio.__remoteUploadResult.preview_url : studio.__uploadlyPreviewUrl || libraryPreviewUrl,
      cropped_preview_file: croppedGenerated ? "Attached" : "Not generated by browser",
      material: studio.material ? studio.material.label : "",
      size: typeof studio.displaySize === "function" ? studio.displaySize(studio.size) : studio.size,
      paper_finish: studio.showPaperFinishes && studio.showPaperFinishes() ? studio.paperFinish : "",
      frame: studio.frameMode || "",
      frame_colour: studio.showFrameColours && studio.showFrameColours() ? studio.frameColour : "",
      canvas_depth: studio.material && studio.material.type === "canvas" ? studio.canvasDepth : "",
      orientation: studio.printOrientation === "landscape" ? "Landscape" : "Portrait",
      crop_mode: cropMode,
      white_border: whiteBorder,
      applied_side_border_inches: borderPreset ? String(Math.round(appliedSideBorder * 100) / 100) : "",
      crop_zoom: borderPreset || !zoomInput ? "100%" : `${zoomInput.value}%`,
      crop_position_x: borderPreset || !panXInput ? "0%" : `${panXInput.value}%`,
      crop_position_y: borderPreset || !panYInput ? "0%" : `${panYInput.value}%`,
    };
  }

  function cartRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function previewAwareUrl(path) {
    const previewThemeId = new URLSearchParams(window.location.search).get("preview_theme_id");
    if (!previewThemeId) return path;
    const url = new URL(path, window.location.origin);
    url.searchParams.set("preview_theme_id", previewThemeId);
    return `${url.pathname}${url.search}`;
  }

  function priceCents(studio) {
    const variant = typeof studio.variant === "function" ? studio.variant() : null;
    if (!variant) return 0;
    const raw = String(variant.price || "0");
    const amount = Number(raw);
    return raw.includes(".") ? Math.round(amount * 100) : Math.round(amount);
  }

  function quantity(studio) {
    const input = qs(studio, "[data-quantity]");
    const value = input ? Number(input.value) : 1;
    return Math.max(1, Math.floor(value || 1));
  }

  function money(studio, cents) {
    if (typeof studio.money === "function") return studio.money(cents);
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
  }

  function propertyLabel(name) {
    const match = /^properties\[(.*)\]$/.exec(name || "");
    return match ? match[1] : "";
  }

  function shouldHideLineProperty(label, value) {
    const normalized = String(label || "").trim().toLowerCase();
    const stringValue = typeof value === "string" ? value.toLowerCase() : "";
    if (!normalized || normalized.charAt(0) === "_") return false;
    if (normalized === "fine art print" || normalized.indexOf("fine art print ") === 0) return true;
    if (normalized === "artwork file" || normalized === "proof before production") return true;
    if (normalized === "orientation") return true;
    if (normalized.indexOf("crop ") === 0 || normalized.indexOf("preview ") === 0) return true;
    if (normalized.indexOf("original ") === 0 || normalized.indexOf("production ") === 0 || normalized.indexOf("artwork upload") === 0 || normalized.indexOf("artwork storage") === 0) return true;
    if (normalized.indexOf("converted ") === 0 || normalized.indexOf("cropped ") === 0 || normalized.indexOf("test upload") === 0) return true;
    return /upstorage|uploadcare|ucarecdn|supabase|uploadly-files/.test(stringValue);
  }

  function appendFormData(formData, name, value) {
    if (value instanceof File) formData.append(name, value, value.name);
    else formData.append(name, value);
  }

  function hideInternalLineProperties(formData) {
    const names = Array.from(new Set(Array.from(formData.keys())));
    names.forEach((name) => {
      const label = propertyLabel(name);
      if (!label) return;
      const values = formData.getAll(name);
      if (!values.some((value) => shouldHideLineProperty(label, value))) return;
      const hiddenName = `properties[_${label.replace(/^_+/, "")}]`;
      formData.delete(name);
      values.forEach((value) => appendFormData(formData, hiddenName, value));
    });
  }

  function ensureUploadcareOrderProperties(form, result) {
    const uploadcareUrl = result && result.uploadly_url ? result.uploadly_url : "";
    const previewUrl = result && result.preview_url ? result.preview_url : "";
    const originalUrl = result && result.secure_url ? result.secure_url : uploadcareUrl;
    ensureRemoteInput(form, "properties[_Fine art print]", "uploadcare-fine-art-print", uploadcareUrl);
    ensureRemoteInput(form, "properties[_Original artwork URL]", "original-artwork-url", originalUrl);
    ensureRemoteInput(form, "properties[_Converted preview URL]", "converted-preview-url", previewUrl);
    ensureRemoteInput(form, "properties[_Artwork upload service]", "artwork-upload-service", uploadcareUrl ? "Uploadcare direct upload" : "");
  }

  function ensureCartModalStyles() {
    if (document.getElementById("print-studio-cart-modal-styles")) return;
    const style = document.createElement("style");
    style.id = "print-studio-cart-modal-styles";
    style.textContent = ".print-studio__cart-modal{position:fixed;inset:0;z-index:9999;display:grid;place-items:center;padding:22px;background:rgba(32,34,41,.42);backdrop-filter:blur(10px)}.print-studio__cart-modal[hidden]{display:none}.print-studio__cart-dialog{width:min(100%,480px);background:#fff;color:#202229;border:1px solid rgba(32,34,41,.16);box-shadow:0 28px 80px rgba(17,18,22,.28)}.print-studio__cart-dialog-inner{display:grid;gap:18px;padding:clamp(22px,4vw,34px)}.print-studio__cart-kicker{margin:0;color:#d95f3d;font-size:.78rem;font-weight:900;text-transform:uppercase;letter-spacing:0}.print-studio__cart-title{margin:0;font-size:clamp(1.75rem,5vw,2.6rem);line-height:1}.print-studio__cart-summary{display:grid;gap:10px;padding:14px 0;border-top:1px solid rgba(32,34,41,.16);border-bottom:1px solid rgba(32,34,41,.16)}.print-studio__cart-line{display:flex;align-items:center;justify-content:space-between;gap:16px}.print-studio__cart-line span{color:#646970;font-weight:800}.print-studio__cart-line strong{font-weight:950;text-align:right}.print-studio__cart-actions{display:grid;grid-template-columns:1fr;gap:10px}.print-studio__cart-actions a,.print-studio__cart-actions button{display:grid;min-height:48px;place-items:center;border:1px solid #202229;background:#202229;color:#fff;text-decoration:none;font:inherit;font-weight:900;cursor:pointer}.print-studio__cart-actions a:nth-child(2){background:#fff;color:#202229}.print-studio__cart-actions button{background:#f6f3ed;color:#202229}.print-studio__cart-actions a:focus-visible,.print-studio__cart-actions button:focus-visible{outline:3px solid rgba(217,95,61,.42);outline-offset:2px}@media(min-width:560px){.print-studio__cart-actions{grid-template-columns:1fr 1fr}.print-studio__cart-actions a:last-child{grid-column:1/-1}}";
    document.head.append(style);
  }

  function ensureCartModal(studio) {
    let modal = qs(studio, "[data-cart-modal]");
    if (modal) return modal;
    modal = document.createElement("div");
    modal.className = "print-studio__cart-modal";
    modal.hidden = true;
    modal.setAttribute("data-cart-modal", "");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "print-studio-cart-title");
    modal.innerHTML = '<div class="print-studio__cart-dialog"><div class="print-studio__cart-dialog-inner"><p class="print-studio__cart-kicker">Added to cart</p><h2 class="print-studio__cart-title" id="print-studio-cart-title">Ready to check out?</h2><div class="print-studio__cart-summary"><div class="print-studio__cart-line"><span>Custom print</span><strong data-cart-modal-item>Custom print x 1</strong></div><div class="print-studio__cart-line"><span>Total price</span><strong data-cart-modal-total>$0.00</strong></div></div><div class="print-studio__cart-actions"><button type="button" data-make-another>Make another print</button><a href="/pages/905-prints-and-products" data-view-products>View Products</a><a href="/checkout" data-checkout>Checkout</a></div></div></div>';
    studio.append(modal);
    const makeAnother = qs(modal, "[data-make-another]");
    const viewProducts = qs(modal, "[data-view-products]");
    const checkout = qs(modal, "[data-checkout]");
    if (viewProducts) viewProducts.href = previewAwareUrl("/pages/905-prints-and-products");
    if (checkout) checkout.href = `${cartRoot()}checkout`.replace("//", "/");
    if (makeAnother) {
      makeAnother.addEventListener("click", () => {
        window.location.href = window.location.href;
      });
    }
    modal.addEventListener("click", (event) => {
      if (event.target === modal) modal.hidden = true;
    });
    return modal;
  }

  function bindCartModal(studio) {
    ensureCartModalStyles();
    ensureCartModal(studio);
    if (studio.__cartModalMethodsBound) return;
    studio.__cartModalMethodsBound = true;
    studio.showCartAddedModal = function () {
      const modal = ensureCartModal(this);
      const qty = quantity(this);
      const item = qs(modal, "[data-cart-modal-item]");
      const total = qs(modal, "[data-cart-modal-total]");
      if (item) item.textContent = `Custom print x ${qty}`;
      if (total) total.textContent = money(this, priceCents(this) * qty);
      modal.hidden = false;
      const firstAction = qs(modal, "[data-make-another]");
      if (firstAction) firstAction.focus();
    };
    studio.addPreparedOrderToCart = async function (form) {
      const formData = new FormData(form);
      hideInternalLineProperties(formData);
      formData.set("sections", "cart-ajax");
      const response = await fetch(`${cartRoot()}cart/add.js`, {
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw data;
      document.dispatchEvent(new CustomEvent("ajaxProduct:added", { bubbles: true, detail: { product: data } }));
      this.showCartAddedModal();
      return data;
    };
  }

  async function prepareProductionFiles(studio) {
    const form = studio.form || qs(studio, "[data-form]");
    const upload = qs(studio, "[data-upload]");
    if (!form || !upload) return;

    const uploadlyActive = setUploadlyMode(studio);
    const directUploadcare = studio.classList.contains("uses-direct-uploadcare");
    const directFile = directUploadcare ? studio.__uploadcareFile || (upload.files && upload.files[0] ? upload.files[0] : null) : null;
    const originalFile = !uploadlyActive && upload.files && upload.files[0] ? upload.files[0] : null;
    const libraryArtwork = studio.__artistLibraryArtwork || null;
    const libraryOriginalUrl = libraryArtwork && libraryArtwork.originalUrl ? libraryArtwork.originalUrl : "";
    const libraryPreviewUrl = libraryArtwork && libraryArtwork.previewUrl ? libraryArtwork.previewUrl : "";
    const productionFile = directFile || originalFile;
    const uploadcareUpload = directFile && ensureUploadcareUpload(studio, directFile) ? await ensureUploadcareUpload(studio, directFile) : null;
    const remoteUpload = originalFile && ensureRemoteUpload(studio, originalFile) ? await ensureRemoteUpload(studio, originalFile) : null;
    const remotePreviewUrl = uploadcareUpload && uploadcareUpload.preview_url ? uploadcareUpload.preview_url : remoteUpload && remoteUpload.preview_url ? remoteUpload.preview_url : studio.__uploadlyPreviewUrl || uploadlyPreviewUrl(studio) || libraryPreviewUrl;
    const cropSettingsInput = ensureHiddenInput(form, "properties[_Crop settings]", "crop-settings");
    const previewStatusInput = ensureHiddenInput(form, "properties[_Cropped preview status]", "cropped-preview-status");
    const croppedInput = ensureCroppedFileInput(form);
    const originalUrlInput = ensureRemoteInput(form, "properties[_Original artwork URL]", "original-artwork-url", remoteUpload && remoteUpload.secure_url ? remoteUpload.secure_url : libraryOriginalUrl);
    const previewUrlInput = ensureRemoteInput(form, "properties[_Converted preview URL]", "converted-preview-url", remotePreviewUrl);
    const serviceInput = ensureRemoteInput(form, "properties[_Artwork upload service]", "artwork-upload-service", libraryArtwork ? "Artist library" : "");
    if (uploadcareUpload) ensureUploadcareOrderProperties(form, uploadcareUpload);

    let croppedFile = null;
    if (productionFile || remotePreviewUrl) {
      try {
        croppedFile = await buildCroppedPreviewFile(studio, canBrowserPreview(productionFile) ? productionFile : null, remotePreviewUrl);
      } catch (error) {
        croppedFile = null;
      }
    }

    if (croppedFile && applyFilesToInput(croppedInput, [croppedFile])) {
      croppedInput.disabled = false;
      previewStatusInput.value = "Cropped preview file attached.";
    } else {
      croppedInput.disabled = true;
      previewStatusInput.value = uploadcareUpload ? "Original production file stored with Uploadcare. Browser could not generate cropped preview." : libraryArtwork ? "Original artwork loaded from artist library. Browser could not generate cropped preview." : uploadlyActive ? "Original production file handled by Uploadly. Browser could not generate cropped preview." : originalFile ? "Original file attached. Browser could not generate cropped preview for this file type." : "No artwork file uploaded.";
    }

    const zoomInput = qs(studio, "[data-zoom]");
    const panXInput = qs(studio, "[data-pan-x]");
    const panYInput = qs(studio, "[data-pan-y]");
    const zoomProp = qs(form, '[data-prop="zoom"]');
    if (zoomProp) {
      zoomProp.disabled = false;
      zoomProp.name = "properties[_Preview zoom]";
      zoomProp.value = zoomInput ? `${zoomInput.value}%` : "100%";
    }
    const panXProp = qs(form, '[data-prop="panX"]');
    if (panXProp) {
      panXProp.disabled = false;
      panXProp.name = "properties[_Preview horizontal position]";
      panXProp.value = panXInput ? `${panXInput.value}%` : "0%";
    }
    const panYProp = qs(form, '[data-prop="panY"]');
    if (panYProp) {
      panYProp.disabled = false;
      panYProp.name = "properties[_Preview vertical position]";
      panYProp.value = panYInput ? `${panYInput.value}%` : "0%";
    }

    cropSettingsInput.value = JSON.stringify(productionSettings(studio, productionFile, Boolean(croppedFile)));

    if (remoteUpload && remoteUpload.secure_url && !directUploadcare) upload.disabled = true;
  }

  function patchPreview(Studio) {
    const proto = Studio && Studio.prototype;
    if (!proto || proto.__productionUploadPatched) return;
    proto.__productionUploadPatched = true;

    const originalPreview = proto.preview;
    proto.preview = function (event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.__artistLibraryArtwork = null;
      this.classList.remove("has-artist-library-artwork");

      if (this.classList.contains("uses-direct-uploadcare")) {
        this.__uploadcareFile = null;
        this.__uploadcarePromise = null;
        this.__uploadcareResult = null;
        const uploadcarePromise = ensureUploadcareUpload(this, file);

        if (!canBrowserPreview(file) || file.size > LOCAL_PREVIEW_MAX_BYTES) {
          this.artRatio = 0;
          this.artNaturalWidth = 0;
          this.artNaturalHeight = 0;
          if (typeof this.renderSizes === "function") this.renderSizes();
          if (typeof this.updateFitCopy === "function") this.updateFitCopy();
          status(this, "Uploading original artwork for production and preparing a browser-friendly preview...");
          if (uploadcarePromise) {
            uploadcarePromise.then((result) => {
              if (result && result.preview_url) setPreviewFromImage(this, result.preview_url);
            }).catch(() => {});
          }
          return;
        }

        setPreviewFromFile(this, file, "Preview ready. Uploading original artwork for production.");
        if (uploadcarePromise) uploadcarePromise.catch(() => {});
        return;
      }

      const remotePromise = ensureRemoteUpload(this, file);

      if (!canBrowserPreview(file)) {
        this.artRatio = 0;
        this.artNaturalWidth = 0;
        this.artNaturalHeight = 0;
        if (typeof this.renderSizes === "function") this.renderSizes();
        if (typeof this.updateFitCopy === "function") this.updateFitCopy();
        status(this, remotePromise ? "Converting a preview copy for the browser..." : "File attached for production. This format may not preview or generate a cropped preview in the browser.");
        if (remotePromise) {
          remotePromise.then((result) => {
            if (result && result.preview_url) setPreviewFromImage(this, result.preview_url);
          });
        }
        return;
      }

      if (originalPreview) originalPreview.call(this, event);
      if (remotePromise) {
        remotePromise.then((result) => {
          if (result && result.secure_url) status(this, "Preview ready. Original file is stored for production.");
        });
      }
    };
  }

  function bindSubmit(studio) {
    const form = studio.form || qs(studio, "[data-form]");
    if (!form || form.dataset.productionUploadBound) return;
    form.dataset.productionUploadBound = "true";
    ensureCroppedFileInput(form).disabled = true;

    const submitOrder = async (event) => {
      if (event.defaultPrevented || form.dataset.productionSubmitting === "true") return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation();
      releaseUploadlySubmitControl(studio);

      const variantId = qs(studio, "[data-variant-id]");
      if (!variantId || !variantId.value) {
        status(studio, "This combination needs a matching active Shopify variant before checkout.");
        return;
      }
      const upload = qs(studio, "[data-upload]");
      if (studio.classList.contains("uses-direct-uploadcare") && !(studio.__uploadcareFile || (upload && upload.files && upload.files[0]) || studio.__artistLibraryArtwork)) {
        status(studio, "Please upload your artwork before adding this print to the cart.");
        if (typeof studio.go === "function") studio.go(1);
        return;
      }

      status(studio, "Preparing artwork files for production...");
      try {
        await prepareProductionFiles(studio);
        form.dataset.productionSubmitting = "true";
        if (typeof studio.addPreparedOrderToCart === "function") {
          await studio.addPreparedOrderToCart(form);
          form.dataset.productionSubmitting = "false";
          status(studio, "Added to cart.");
        } else {
          HTMLFormElement.prototype.submit.call(form);
        }
      } catch (error) {
        form.dataset.productionSubmitting = "false";
        const message = error && (error.description || error.message) ? error.description || error.message : "Something went wrong while adding this print to the cart.";
        status(studio, message);
      }
    };

    form.addEventListener("submit", submitOrder);
    const submit = qs(studio, "[data-submit]");
    if (submit && !submit.dataset.productionClickBound) {
      submit.dataset.productionClickBound = "true";
      submit.addEventListener("click", submitOrder, true);
    }
  }

  function initStudio(studio) {
    ensureUploadlyStyles();
    bindCartModal(studio);
    updateUploadCopy(studio);
    bindUploadlyPreview(studio);
    watchUploadlySubmitControl(studio);
    bindSubmit(studio);
    preloadArtistLibraryArtwork(studio);
  }

  function init() {
    const Studio = customElements.get("print-frame-studio");
    patchPreview(Studio);
    document.querySelectorAll("print-frame-studio").forEach(initStudio);
  }

  if (window.customElements) customElements.whenDefined("print-frame-studio").then(init);
})();
